import React, { useState, useEffect, useCallback } from 'react';
import { commonStyles } from '../../../../common/components/Layout/DashboardLayout';
import classNames from 'classnames';
import { useChowaTheme } from '../../config/chowaThemeContext';

const SettingField = ({ label, value, onChange, readOnly = false }) => (
  <div className="mb-4">
    <label className={commonStyles.form.label}>
      {label}
    </label>
    <input
      type="number"
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      className={classNames(
        commonStyles.form.input,
        readOnly && 'bg-gray-50 dark:bg-gray-700'
      )}
    />
  </div>
);

const SettingsForm = ({ wsStatus, setWsStatus, modelId = 'tms000' }) => {
  const { updateTmsConnectionState, resetTmsConnectionState } = useChowaTheme();
  
  // modelIdから表示名を取得
  const getDisplayName = (id) => {
    const upperModel = id.replace('tms', 'TMS#').toUpperCase();
    return upperModel;
  };
  const [socket, setSocket] = useState(null);
  const [currentSettings, setCurrentSettings] = useState({
    upperLimit: '',
    lowerLimit: '',
    csvSaveTime: '',
    ftpSendInterval: '',
    apiSendInterval: '',
    signalStrengthThreshold: '',
    deviceType: 'None'
  });

  const [newSettings, setNewSettings] = useState({
    upperLimit: '',
    lowerLimit: '',
    csvSaveTime: '',
    ftpSendInterval: '',
    apiSendInterval: '',
    signalStrengthThreshold: ''
  });

  const [loading, setLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [countdownTimer, setCountdownTimer] = useState(null);

  // 状態を完全にリセットする関数（最初に定義）
  const resetConnectionState = useCallback(() => {
    setSocket(null);
    setCountdown(0);
    setIsConnecting(false);
    setWsStatus('未接続');
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
  }, [countdownTimer, setWsStatus]);

  const connectWebSocket = useCallback(() => {
    if (isConnecting || socket) return;
    
    setIsConnecting(true);
    setWsStatus('接続試行中');
    setCountdown(0); // 接続開始時にカウントダウンリセット
    
    // 動的WebSocket URL生成
    const wsUrl = `wss://ksk-aircloud.com/ws/mqtt/${modelId}/`;
    const ws = new WebSocket(wsUrl);
    
    // 接続タイムアウト処理（10秒）
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
        resetConnectionState(); // 完全リセット
        setWsStatus('接続タイムアウト'); // タイムアウト状態のみ設定
      }
    }, 10000);
    
    ws.onopen = () => {
      clearTimeout(connectionTimeout); // タイムアウトをクリア
      setSocket(ws);
      setWsStatus('接続中');
      setIsConnecting(false);
      ws.send(JSON.stringify({ action: 'get_config' }));
      
      // 1分カウントダウン開始
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // カウントダウン終了 - 自動切断
            clearInterval(timer);
            ws.close();
            setCountdownTimer(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setCountdownTimer(timer);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'config_update') {
        const updatedConfig = {
          upperLimit: data.config.上限値?.toString() || '',
          lowerLimit: data.config.下限値?.toString() || '',
          csvSaveTime: data.config.CSV保存時間?.toString() || '',
          ftpSendInterval: data.config.FTPファイル送信間隔?.toString() || '',
          apiSendInterval: data.config.API送信間隔?.toString() || '',
          signalStrengthThreshold: data.config.電波強度閾値?.toString() || '',
          deviceType: data.config.device_type || 'None'
        };
        setCurrentSettings(updatedConfig);
        setNewSettings(updatedConfig);
        setLoading(false);
      } else if (data.type === 'status_update') {
        setWsStatus(data.ws_status);
      }
    };
    
    ws.onclose = () => {
      clearTimeout(connectionTimeout); // タイムアウトをクリア
      resetConnectionState(); // 完全リセット
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
      clearTimeout(connectionTimeout); // タイムアウトをクリア
      resetConnectionState(); // 完全リセット
      setWsStatus('接続エラー'); // エラー状態のみ設定
    };
    
    return ws;
  }, [setWsStatus, modelId, isConnecting, socket, resetConnectionState]);

  const disconnectWebSocket = useCallback(() => {
    if (socket) {
      socket.close();
    }
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    // 完全な状態リセット
    resetConnectionState();
  }, [socket, countdownTimer, resetConnectionState]);

  // modelIdが変更された時に設定値をクリア
  useEffect(() => {
    // 設定値を初期状態にリセット
    const initialSettings = {
      upperLimit: '',
      lowerLimit: '',
      csvSaveTime: '',
      ftpSendInterval: '',
      apiSendInterval: '',
      signalStrengthThreshold: '',
      deviceType: 'None'
    };
    
    const initialNewSettings = {
      upperLimit: '',
      lowerLimit: '',
      csvSaveTime: '',
      ftpSendInterval: '',
      apiSendInterval: '',
      signalStrengthThreshold: ''
    };
    
    setCurrentSettings(initialSettings);
    setNewSettings(initialNewSettings);
    setLoading(false);
  }, [modelId]);

  // 接続状態をContextに同期
  useEffect(() => {
    const isConnected = wsStatus === '接続中';
    updateTmsConnectionState({
      isConnected,
      currentModelId: modelId,
      wsStatus,
      countdown
    });
  }, [wsStatus, modelId, countdown, updateTmsConnectionState]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      // コンポーネントアンマウント時に接続状態をリセット
      resetTmsConnectionState();
    };
  }, [countdownTimer, resetTmsConnectionState]);

  const handleNewSettingChange = (field) => (e) => {
    setNewSettings(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleFetchCurrentSettings = () => {
    setLoading(true);
    if (socket) {
      socket.send(JSON.stringify({ action: 'get_config' }));
    } else {
      alert('WebSocket接続がありません。');
      setLoading(false);
    }
  };

  const handleUpdateSettings = (e) => {
    e.preventDefault();
    setLoading(true);

    if (parseInt(newSettings.upperLimit) > 150) {
      alert('上限値は150以内で設定してください');
      setLoading(false);
      return;
    }

    if (parseInt(newSettings.lowerLimit) >= parseInt(newSettings.upperLimit)) {
      alert('下限値 < 上限値 としてください。');
      setLoading(false);
      return;
    }

    const configData = {
      "上限値": parseInt(newSettings.upperLimit) || 0,
      "下限値": parseInt(newSettings.lowerLimit) || 0,
      "CSV保存時間": parseInt(newSettings.csvSaveTime) || 0,
      "FTPファイル送信間隔": parseInt(newSettings.ftpSendInterval) || 0,
      "API送信間隔": parseInt(newSettings.apiSendInterval) || 0,
      "電波強度閾値": parseInt(newSettings.signalStrengthThreshold) || 0
    };

    if (socket) {
      socket.send(JSON.stringify({ action: 'send_config', config: configData }));
    } else {
      alert('WebSocket接続がありません。');
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <h2 className={classNames(commonStyles.text.title, "mb-4 lg:mb-6 text-center")}>
        {getDisplayName(modelId)} Config
      </h2>
      
      {/* 接続コントロール */}
      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={classNames(
              "w-3 h-3 rounded-full",
              wsStatus === '接続中' ? "bg-green-500" :
              wsStatus === '接続試行中' ? "bg-yellow-500 animate-pulse" :
              wsStatus === '接続エラー' ? "bg-red-500" :
              wsStatus === '接続タイムアウト' ? "bg-orange-500" : "bg-gray-400"
            )}></div>
            <span className={classNames(commonStyles.text.body, "font-medium")}>
              {wsStatus}
            </span>
            {countdown > 0 && (
              <span className={classNames(commonStyles.text.body, "text-blue-600 dark:text-blue-400")}>
                (自動切断まで: {countdown}秒)
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            {!socket && !isConnecting && (
              <button
                onClick={connectWebSocket}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
              >
                接続
              </button>
            )}
            {socket && (
              <button
                onClick={disconnectWebSocket}
                className="px-4 py-2 text-sm bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-md transition-colors"
              >
                切断
              </button>
            )}
            {isConnecting && (
              <button
                disabled
                className="px-4 py-2 text-sm bg-blue-600 text-white font-medium rounded-md opacity-50 cursor-not-allowed"
              >
                接続中...
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 overflow-auto">
        {/* 現在の設定 */}
        <div className={classNames(
          commonStyles.card.padding.default,
          "bg-white/50 dark:bg-gray-800/50"
        )}>
          <h3 className={classNames(commonStyles.text.subtitle, "mb-3 lg:mb-4 text-center")}>
            Now Config
          </h3>
          <div className="space-y-3 lg:space-y-4">
            <div className="space-y-4 lg:space-y-6">
              <SettingField 
                label="上限値："
                value={currentSettings.upperLimit}
                readOnly
              />
              <SettingField 
                label="下限値："
                value={currentSettings.lowerLimit}
                readOnly
              />
              <SettingField 
                label="CSV保存時間 (秒)："
                value={currentSettings.csvSaveTime}
                readOnly
              />
              <SettingField 
                label="FTP送信間隔 (秒)："
                value={currentSettings.ftpSendInterval}
                readOnly
              />
              <SettingField 
                label="API送信間隔 (秒)："
                value={currentSettings.apiSendInterval}
                readOnly
              />
              <SettingField 
                label="電波強度閾値 (dBm)："
                value={currentSettings.signalStrengthThreshold}
                readOnly
              />
            </div>
            <button
              onClick={handleFetchCurrentSettings}
              disabled={loading}
              className={classNames(
                commonStyles.form.button,
                "w-full bg-green-600 hover:bg-green-700",
                loading && "opacity-50 cursor-not-allowed"
              )}
            >
              現在値を再取得
            </button>
          </div>
        </div>

        {/* 新しい設定 */}
        <div className={classNames(
          commonStyles.card.padding.default,
          "bg-white/50 dark:bg-gray-800/50"
        )}>
          <h3 className={classNames(commonStyles.text.subtitle, "mb-3 lg:mb-4 text-center")}>
            New Config
          </h3>
          <form onSubmit={handleUpdateSettings}>
            <div className="space-y-3 lg:space-y-4">
              <div className="space-y-4 lg:space-y-6">
                <SettingField 
                  label="上限値："
                  value={newSettings.upperLimit}
                  onChange={handleNewSettingChange('upperLimit')}
                />
                <SettingField 
                  label="下限値："
                  value={newSettings.lowerLimit}
                  onChange={handleNewSettingChange('lowerLimit')}
                />
                <SettingField 
                  label="CSV保存時間 (秒)："
                  value={newSettings.csvSaveTime}
                  onChange={handleNewSettingChange('csvSaveTime')}
                />
                <SettingField 
                  label="FTP送信間隔 (秒)："
                  value={newSettings.ftpSendInterval}
                  onChange={handleNewSettingChange('ftpSendInterval')}
                />
                <SettingField 
                  label="API送信間隔 (秒)："
                  value={newSettings.apiSendInterval}
                  onChange={handleNewSettingChange('apiSendInterval')}
                />
                <SettingField 
                  label="電波強度閾値 (dBm)："
                  value={newSettings.signalStrengthThreshold}
                  onChange={handleNewSettingChange('signalStrengthThreshold')}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={classNames(
                  commonStyles.form.button,
                  "w-full bg-green-600 hover:bg-green-700",
                  loading && "opacity-50 cursor-not-allowed"
                )}
              >
                設定を更新
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-auto pt-2 lg:pt-4">
        <span className={commonStyles.text.body}>
          最終更新: {currentSettings.deviceType}
        </span>
      </div>
    </div>
  );
};

export default SettingsForm; 